import React, { useState, useEffect, useCallback } from 'react';
import { StatusBadge } from '../shared';
import api from '../../utils/api';

export default function LeadsPage({ openModal }) {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [expanded, setExpanded] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/adani/leads?limit=100&skip=0');
      const body = res.data;
      let list = [];
      if (Array.isArray(body)) list = body;
      else if (body && Array.isArray(body.data)) list = body.data;
      else if (body && Array.isArray(body.leads)) list = body.leads;
      else if (body && typeof body === 'object') {
        const arr = Object.values(body).find(v => Array.isArray(v));
        if (arr) list = arr;
      }

      const mapped = list.map(l => {
        const rawId = (typeof l._id === 'object' && l._id?.$oid) ? l._id.$oid : (l._id || l.id || Math.random().toString(36).slice(2));
        return {
          id: rawId,
          name: l.name || l.full_name || '',
          company: l.company || '',
          designation: l.designation || '',
          phone: l.phone || '',
          email: l.email || '',
          notes: l.notes || '',
          source: l.source || l.recording_id || '',
          status: l.status || 'New',
          date: l.date || l.created_at || '',
        };
      });
      setLeads(mapped);
    } catch (err) {
      console.error('Failed to fetch leads:', err);
      setError('Could not load leads');
      setLeads([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const filtered = leads.filter(l => {
    if (statusFilter !== 'All' && l.status !== statusFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        l.name.toLowerCase().includes(q) ||
        l.company.toLowerCase().includes(q) ||
        l.email.toLowerCase().includes(q) ||
        l.designation.toLowerCase().includes(q) ||
        l.phone.includes(q)
      );
    }
    return true;
  });

  function initials(name) {
    return name.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2);
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="font-google text-[22px] font-semibold text-[var(--adani-text-primary)] leading-tight">
            Leads Management
          </h1>
          <p className="text-[13px] text-[var(--adani-text-secondary)] mt-1">
            Manage leads extracted from recordings and uploads
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={fetchLeads} disabled={loading} className="btn-secondary text-[12px] px-3 py-2" title="Refresh">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={loading ? 'animate-spin' : ''}>
              <path d="M23 4v6h-6M1 20v-6h6" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div className="relative">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--adani-text-tertiary)]">
              <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
            </svg>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search leads…"
              className="w-56 rounded-lg pl-9 pr-3 py-2 text-[13px] text-[var(--adani-text-primary)] placeholder-[var(--adani-text-tertiary)] glass-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            className="topbar-filter"
          >
            {['All', 'New', 'Contacted', 'Qualified'].map(s => <option key={s}>{s}</option>)}
          </select>
          <button onClick={() => openModal('new-lead')} className="btn-primary text-[13px]">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            New Lead
          </button>
        </div>
      </div>

      {error && (
        <div className="glass-card-static rounded-xl p-4 mb-5 flex items-center gap-2">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--adani-danger)" strokeWidth="2"><path d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/></svg>
          <span className="text-[13px] text-[var(--adani-text-secondary)]">{error}</span>
          <button onClick={fetchLeads} className="text-[12px] font-medium text-[var(--adani-primary)] ml-auto cursor-pointer">Retry</button>
        </div>
      )}

      {loading && leads.length === 0 && (
        <div className="flex items-center justify-center gap-2 py-16">
          <div className="h-5 w-5 rounded-full border-2 border-[var(--adani-primary)]/30 border-t-[var(--adani-primary)]" style={{ animation: 'pipSpin 0.8s linear infinite' }} />
          <span className="text-[13px] text-[var(--adani-text-tertiary)]">Loading leads…</span>
        </div>
      )}

      <div className="space-y-3">
        {filtered.map(l => (
          <div
            key={l.id}
            className="glass-card rounded-2xl overflow-hidden cursor-pointer"
            onClick={() => setExpanded(expanded === l.id ? null : l.id)}
          >
            <div className="px-5 py-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3.5">
                  <div className="avatar avatar-md avatar-primary text-[13px]">
                    {initials(l.name) || '?'}
                  </div>
                  <div>
                    <div className="flex items-center gap-2.5">
                      <p className="text-[14px] font-medium text-[var(--adani-text-primary)]">{l.name || 'Unknown'}</p>
                      <StatusBadge status={l.status} />
                    </div>
                    <p className="text-[12px] text-[var(--adani-text-secondary)] mt-0.5">
                      {[l.designation, l.company].filter(Boolean).join(' · ') || '—'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    {l.date && <p className="text-[12px] text-[var(--adani-text-tertiary)]">{l.date}</p>}
                    {l.source && <p className="text-[12px] font-medium text-[var(--adani-primary)] mt-0.5">{l.source}</p>}
                  </div>
                  <svg
                    width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                    className="text-[var(--adani-text-tertiary)] transition-transform"
                    style={{ transform: expanded === l.id ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  >
                    <path d="M6 9l6 6 6-6"/>
                  </svg>
                </div>
              </div>
            </div>

            {expanded === l.id && (
              <div className="px-5 pb-4 pt-0 border-t border-[var(--adani-border)] animate-slide-up">
                <div className="pt-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="glass-card-static rounded-xl p-3.5">
                      <span className="text-overline text-[10px]">Full Name</span>
                      <p className="text-[13px] font-medium text-[var(--adani-text-primary)] mt-1">{l.name || '—'}</p>
                    </div>
                    <div className="glass-card-static rounded-xl p-3.5">
                      <span className="text-overline text-[10px]">Company</span>
                      <p className="text-[13px] font-medium text-[var(--adani-text-primary)] mt-1">{l.company || '—'}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="glass-card-static rounded-xl p-3.5">
                      <span className="text-overline text-[10px]">Designation</span>
                      <p className="text-[13px] font-medium text-[var(--adani-text-primary)] mt-1">{l.designation || '—'}</p>
                    </div>
                    <div className="glass-card-static rounded-xl p-3.5">
                      <span className="text-overline text-[10px]">Phone</span>
                      <p className="text-[13px] font-medium text-[var(--adani-text-primary)] mt-1">{l.phone || '—'}</p>
                    </div>
                    <div className="glass-card-static rounded-xl p-3.5">
                      <span className="text-overline text-[10px]">Email</span>
                      <p className="text-[13px] font-medium text-[var(--adani-text-primary)] mt-1">{l.email || '—'}</p>
                    </div>
                  </div>
                  {l.notes && (
                    <div className="glass-card-static rounded-xl p-3.5">
                      <span className="text-overline text-[10px]">Notes</span>
                      <p className="text-[13px] text-[var(--adani-text-secondary)] mt-1 leading-relaxed">{l.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        {!loading && filtered.length === 0 && (
          <div className="glass-card-static rounded-2xl">
            <div className="empty-state">
              <div className="empty-state-icon">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                </svg>
              </div>
              <p className="empty-state-title">No leads found</p>
              <p className="empty-state-text">
                {leads.length === 0
                  ? 'Leads are automatically extracted when you record or upload files'
                  : 'Try adjusting your search or filters'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
